import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import ImageUpload from '@/components/ImageUpload';
import {
  Plus, Trash2, Save, ArrowUp, ArrowDown, Image as ImageIcon,
  Eye, EyeOff, X, FileText, BookOpen, Download, Upload,
} from 'lucide-react';

const EMPTY = {
  slug: '',
  name: '',
  subtitle: '',
  short_description: '',
  description: '',
  price: 0,
  sale_price: null,
  currency: 'NZD',
  inventory_qty: 0,
  category: '',
  collection_name: '',
  colourway_swatch: '',
  status: 'published',
  highlights: [],
  features: [],
  materials: [],
  care_instructions: [],
  heritage_details: [],
  fit_notes: '',
  size_notes: '',
  image_url: '',
  image_base64: '',
  images: [],
  detail_images: [],
  active: true,
  founder_first: true,
  order_index: 0,
};

const readFile = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onerror = rej;
  r.onload = () => res(r.result);
  r.readAsDataURL(file);
});

/* ------------------------------------------------------------------ */
/*  Multi-line list helper                                             */
/* ------------------------------------------------------------------ */
const linesToArr = (t) => (t || '').split('\n').map(s => s.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join('\n');

/* ------------------------------------------------------------------ */
/*  Section header                                                     */
/* ------------------------------------------------------------------ */
const SectionTitle = ({ children }) => (
  <div className="h-eyebrow pb-2 mb-4 border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>{children}</div>
);

/* ------------------------------------------------------------------ */
/*  Template load / save dialog                                        */
/* ------------------------------------------------------------------ */
function TemplateActions({ form, onApplyTemplate }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('load'); // load | save
  const [templates, setTemplates] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [saveCategory, setSaveCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/admin/description-templates');
      setTemplates(data.templates || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const apply = (tpl) => {
    onApplyTemplate({
      description: tpl.description || '',
      features: tpl.features || [],
      materials: tpl.materials || [],
      fit_notes: tpl.fit_notes || '',
      size_notes: tpl.size_notes || '',
      care_instructions: tpl.care_instructions || [],
      heritage_details: tpl.heritage_details || [],
    });
    setOpen(false);
  };

  const saveTemplate = async () => {
    setError('');
    if (!saveName.trim()) { setError('Template name required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/description-templates', {
        name: saveName.trim(),
        category: saveCategory.trim(),
        description: form.description || '',
        features: form.features || [],
        materials: form.materials || [],
        fit_notes: form.fit_notes || '',
        size_notes: form.size_notes || '',
        care_instructions: form.care_instructions || [],
        heritage_details: form.heritage_details || [],
      });
      setSaveName(''); setSaveCategory('');
      await load();
      setMode('load');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/admin/description-templates/${id}`);
    await load();
  };

  return (
    <>
      <button type="button" className="h-btn-outline text-xs" onClick={() => { setMode('load'); setOpen(true); }}
              data-testid="catalog-templates-open">
        <BookOpen size={14} className="mr-1.5" /> Templates
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(75,24,56,0.35)', backdropFilter: 'blur(4px)' }}
             onClick={() => setOpen(false)}>
          <div className="h-panel w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <div>
                <div className="h-eyebrow">Description templates</div>
                <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Reusable content blocks</div>
              </div>
              <button type="button" className="h-btn-outline text-xs" onClick={() => setOpen(false)}>
                <X size={14} className="mr-1.5" /> Close
              </button>
            </div>
            <div className="px-5 pt-3 flex gap-2">
              <button type="button"
                      className={`h-btn-outline text-xs ${mode === 'load' ? 'font-medium' : 'opacity-70'}`}
                      onClick={() => setMode('load')}
                      style={mode === 'load' ? { background: 'rgba(75,24,56,0.09)' } : undefined}
                      data-testid="templates-tab-load">
                <Download size={12} className="mr-1.5" /> Load into product
              </button>
              <button type="button"
                      className={`h-btn-outline text-xs ${mode === 'save' ? 'font-medium' : 'opacity-70'}`}
                      onClick={() => setMode('save')}
                      style={mode === 'save' ? { background: 'rgba(75,24,56,0.09)' } : undefined}
                      data-testid="templates-tab-save">
                <Upload size={12} className="mr-1.5" /> Save current as template
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              {mode === 'load' && (
                <div className="space-y-2">
                  {templates.length === 0 && (
                    <div className="rounded-xl border border-dashed p-6 text-center text-sm opacity-70"
                         style={{ borderColor: 'rgba(75,24,56,0.35)', color: 'var(--heritage-plum)' }}>
                      No templates saved yet. Fill out a product and click &quot;Save as template&quot; to reuse it.
                    </div>
                  )}
                  {templates.map((tpl) => (
                    <div key={tpl.id} className="rounded-xl border p-3 flex items-center gap-3"
                         style={{ borderColor: 'rgba(75,24,56,0.25)', background: '#FBF8F1' }}
                         data-testid={`template-${tpl.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="h-serif text-base leading-tight" style={{ color: 'var(--heritage-plum)' }}>{tpl.name}</div>
                        <div className="text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>
                          {tpl.category || 'Uncategorised'} · {tpl.features?.length || 0} features · {tpl.materials?.length || 0} materials · {tpl.care_instructions?.length || 0} care
                        </div>
                        {tpl.description && (
                          <div className="text-xs opacity-80 mt-1 line-clamp-2" style={{ color: 'var(--heritage-plum)' }}>
                            {tpl.description.slice(0, 140)}{tpl.description.length > 140 ? '...' : ''}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <button type="button" className="h-btn text-xs" onClick={() => apply(tpl)}
                                data-testid={`template-apply-${tpl.id}`}>
                          Apply
                        </button>
                        <button type="button" className="h-btn-outline text-xs" onClick={() => del(tpl.id)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {mode === 'save' && (
                <div className="space-y-3">
                  <div className="text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>
                    Saves the current Description, Features, Materials, Fit, Size, Care and Heritage fields as a reusable template. Slug, name, price, and images are never included.
                  </div>
                  <div>
                    <label className="h-label">Template name</label>
                    <input className="h-input" value={saveName} onChange={(e) => setSaveName(e.target.value)}
                           placeholder="e.g. Signature Hoodie base" data-testid="template-save-name" />
                  </div>
                  <div>
                    <label className="h-label">Category (optional)</label>
                    <input className="h-input" value={saveCategory} onChange={(e) => setSaveCategory(e.target.value)}
                           placeholder="e.g. Hoodie, Pants, Half Zip" data-testid="template-save-category" />
                  </div>
                  {error && <div className="text-sm" style={{ color: '#9A2C2C' }}>{error}</div>}
                  <div className="flex justify-end">
                    <button type="button" className="h-btn" disabled={saving} onClick={saveTemplate}
                            data-testid="template-save-btn">
                      <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save template'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Item Form                                                          */
/* ------------------------------------------------------------------ */
function ItemForm({ initial, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [highlightsText, setHighlightsText] = useState(arrToLines(initial?.highlights));
  const [featuresText, setFeaturesText] = useState(arrToLines(initial?.features));
  const [materialsText, setMaterialsText] = useState(arrToLines(initial?.materials));
  const [careText, setCareText] = useState(arrToLines(initial?.care_instructions));
  const [heritageText, setHeritageText] = useState(arrToLines(initial?.heritage_details));
  const [detailImagesText, setDetailImagesText] = useState(arrToLines(initial?.detail_images));
  const [imagesText, setImagesText] = useState(arrToLines(initial?.images));

  useEffect(() => {
    const src = initial || EMPTY;
    setForm(src);
    setHighlightsText(arrToLines(src.highlights));
    setFeaturesText(arrToLines(src.features));
    setMaterialsText(arrToLines(src.materials));
    setCareText(arrToLines(src.care_instructions));
    setHeritageText(arrToLines(src.heritage_details));
    setDetailImagesText(arrToLines(src.detail_images));
    setImagesText(arrToLines(src.images));
    setError('');
  }, [initial]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_500_000) { alert('Please choose an image under 2.5MB'); return; }
    const b64 = await readFile(f);
    setForm({ ...form, image_base64: b64, image_url: '' });
  };
  const clearImage = () => setForm({ ...form, image_base64: '', image_url: '' });

  const applyTemplate = (tpl) => {
    setForm({ ...form, description: tpl.description, fit_notes: tpl.fit_notes, size_notes: tpl.size_notes });
    setFeaturesText(arrToLines(tpl.features));
    setMaterialsText(arrToLines(tpl.materials));
    setCareText(arrToLines(tpl.care_instructions));
    setHeritageText(arrToLines(tpl.heritage_details));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug.toLowerCase().replace(/\s+/g, '-'),
        highlights: linesToArr(highlightsText),
        features: linesToArr(featuresText),
        materials: linesToArr(materialsText),
        care_instructions: linesToArr(careText),
        heritage_details: linesToArr(heritageText),
        detail_images: linesToArr(detailImagesText),
        images: linesToArr(imagesText),
        price: parseFloat(form.price) || 0,
        sale_price: form.sale_price === '' || form.sale_price === null ? null : parseFloat(form.sale_price),
        inventory_qty: parseInt(form.inventory_qty) || 0,
        order_index: parseInt(form.order_index) || 0,
      };
      await onSave(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const preview = form.image_base64
    ? (form.image_base64.startsWith('data:') ? form.image_base64 : `data:image/png;base64,${form.image_base64}`)
    : (form.image_url || '');

  return (
    <Panel className="p-6">
      <form onSubmit={submit} className="space-y-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="h-eyebrow">{initial?.id ? 'Editing product' : 'New product'}</div>
            <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>
              {form.name || 'Untitled product'}
            </div>
          </div>
          <div className="flex gap-2">
            <TemplateActions form={{ ...form,
              features: linesToArr(featuresText),
              materials: linesToArr(materialsText),
              care_instructions: linesToArr(careText),
              heritage_details: linesToArr(heritageText),
            }} onApplyTemplate={applyTemplate} />
            {onCancel && (
              <button type="button" className="h-btn-outline text-xs" onClick={onCancel} data-testid="catalog-close">
                <X size={14} className="mr-1.5" />Close
              </button>
            )}
          </div>
        </div>

        {/* Basic */}
        <div>
          <SectionTitle>Basic details</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="h-label">Slug (URL id)</label>
              <input className="h-input" required value={form.slug}
                     onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                     data-testid="catalog-slug" />
            </div>
            <div>
              <label className="h-label">Order (display position)</label>
              <input className="h-input" type="number" value={form.order_index}
                     onChange={(e) => setForm({ ...form, order_index: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="h-label">Product name</label>
              <input className="h-input" required value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="catalog-name" />
            </div>
            <div className="md:col-span-2">
              <label className="h-label">Subtitle (e.g. Collection · Colourway)</label>
              <input className="h-input" value={form.subtitle}
                     onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
            </div>
            <div>
              <label className="h-label">Category</label>
              <input className="h-input" value={form.category}
                     onChange={(e) => setForm({ ...form, category: e.target.value })}
                     placeholder="Hoodie, Pants, Half Zip..." data-testid="catalog-category" />
            </div>
            <div>
              <label className="h-label">Collection name</label>
              <input className="h-input" value={form.collection_name}
                     onChange={(e) => setForm({ ...form, collection_name: e.target.value })}
                     placeholder="Heritage Original Collection" />
            </div>
            <div>
              <label className="h-label">Colourway / swatch label</label>
              <input className="h-input" value={form.colourway_swatch}
                     onChange={(e) => setForm({ ...form, colourway_swatch: e.target.value })}
                     placeholder="No. 110 Pinkle" />
            </div>
            <div>
              <label className="h-label">Status</label>
              <select className="h-input" value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="catalog-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <SectionTitle>Descriptions</SectionTitle>
          <div className="space-y-4">
            <div>
              <label className="h-label">Short description (used on catalogue cards)</label>
              <textarea className="h-input" rows={2} value={form.short_description}
                        onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                        data-testid="catalog-short-desc" />
            </div>
            <div>
              <label className="h-label">Full description (product detail page)</label>
              <textarea className="h-input" rows={5} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="catalog-description" />
            </div>
            <div>
              <label className="h-label">Highlights (one per line — shown as bullets on card)</label>
              <textarea className="h-input" rows={4} value={highlightsText}
                        onChange={(e) => setHighlightsText(e.target.value)}
                        data-testid="catalog-highlights" />
            </div>
          </div>
        </div>

        {/* Features & Materials */}
        <div>
          <SectionTitle>Features &amp; materials</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="h-label">Features (one per line)</label>
              <textarea className="h-input" rows={7} value={featuresText}
                        onChange={(e) => setFeaturesText(e.target.value)}
                        data-testid="catalog-features" />
            </div>
            <div>
              <label className="h-label">Materials (one per line)</label>
              <textarea className="h-input" rows={7} value={materialsText}
                        onChange={(e) => setMaterialsText(e.target.value)}
                        data-testid="catalog-materials" />
            </div>
          </div>
        </div>

        {/* Fit & Care */}
        <div>
          <SectionTitle>Fit, size &amp; care</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="h-label">Fit notes</label>
              <textarea className="h-input" rows={3} value={form.fit_notes}
                        onChange={(e) => setForm({ ...form, fit_notes: e.target.value })}
                        data-testid="catalog-fit" />
            </div>
            <div>
              <label className="h-label">Size guide notes</label>
              <textarea className="h-input" rows={3} value={form.size_notes}
                        onChange={(e) => setForm({ ...form, size_notes: e.target.value })}
                        data-testid="catalog-size" />
            </div>
            <div className="md:col-span-2">
              <label className="h-label">Care instructions (one per line)</label>
              <textarea className="h-input" rows={5} value={careText}
                        onChange={(e) => setCareText(e.target.value)}
                        data-testid="catalog-care" />
            </div>
          </div>
        </div>

        {/* Heritage details */}
        <div>
          <SectionTitle>Heritage details</SectionTitle>
          <textarea className="h-input" rows={5} value={heritageText}
                    onChange={(e) => setHeritageText(e.target.value)}
                    data-testid="catalog-heritage"
                    placeholder="One statement per line, e.g. Signature oversized fit" />
        </div>

        {/* Media */}
        <div>
          <SectionTitle>Media</SectionTitle>
          <div className="space-y-4">
            <ImageUpload
              label="Primary image"
              value={form.image_base64 || form.image_url}
              onChange={(url) => {
                if (!url) { setForm({ ...form, image_base64: '', image_url: '' }); return; }
                if (url.startsWith('data:')) setForm({ ...form, image_base64: url, image_url: '' });
                else setForm({ ...form, image_url: url, image_base64: '' });
              }}
              testId="catalog-primary-image"
            />
            <div>
              <label className="h-label">Gallery image URLs (one per line)</label>
              <textarea className="h-input" rows={3} value={imagesText}
                        onChange={(e) => setImagesText(e.target.value)}
                        placeholder="https://..." />
            </div>
            <div>
              <label className="h-label">Detail / lifestyle image URLs (one per line)</label>
              <textarea className="h-input" rows={3} value={detailImagesText}
                        onChange={(e) => setDetailImagesText(e.target.value)}
                        placeholder="https://..." />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div>
          <SectionTitle>Pricing &amp; inventory</SectionTitle>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="h-label">Price</label>
              <input className="h-input" type="number" step="0.01" min="0" value={form.price}
                     onChange={(e) => setForm({ ...form, price: e.target.value })}
                     data-testid="catalog-price" />
            </div>
            <div>
              <label className="h-label">Sale price (optional)</label>
              <input className="h-input" type="number" step="0.01" min="0"
                     value={form.sale_price ?? ''}
                     onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                     placeholder="Leave blank if no sale" />
            </div>
            <div>
              <label className="h-label">Currency</label>
              <input className="h-input" value={form.currency}
                     onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
            <div>
              <label className="h-label">Inventory qty</label>
              <input className="h-input" type="number" min="0" value={form.inventory_qty}
                     onChange={(e) => setForm({ ...form, inventory_qty: e.target.value })}
                     data-testid="catalog-inventory" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 mt-4">
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <input type="checkbox" checked={form.active}
                     onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Published
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <input type="checkbox" checked={form.founder_first}
                     onChange={(e) => setForm({ ...form, founder_first: e.target.checked })} /> Founders first
            </label>
          </div>
        </div>

        {error && <div className="text-sm" style={{ color: '#9A2C2C' }}>{error}</div>}

        <div className="flex flex-wrap gap-2 justify-end">
          {onDelete && initial?.id && (
            <button type="button" className="h-btn-outline text-xs" onClick={() => onDelete(initial.id)}
                    data-testid="catalog-delete">
              <Trash2 size={14} className="mr-1.5" /> Delete
            </button>
          )}
          <button type="submit" className="h-btn" disabled={saving} data-testid="catalog-save">
            <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save product'}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
export default function AdminCatalog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/admin/catalog-items');
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  const save = async (item) => {
    if (item.id) {
      await api.put(`/admin/catalog-items/${item.id}`, item);
      showToast('Product saved');
    } else {
      await api.post('/admin/catalog-items', item);
      showToast('Product created');
    }
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    await api.delete(`/admin/catalog-items/${id}`);
    setEditing(null);
    load();
  };

  const move = async (id, direction) => {
    const idx = items.findIndex(i => i.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = [...items];
    const [it] = next.splice(idx, 1);
    next.splice(newIdx, 0, it);
    setItems(next);
    await api.post('/admin/catalog-items/reorder', { ids: next.map(i => i.id) });
  };

  const toggleActive = async (item) => {
    await api.put(`/admin/catalog-items/${item.id}`, { ...item, active: !item.active });
    load();
  };

  const statusPill = (it) => {
    const status = it.status || (it.active ? 'published' : 'draft');
    const map = {
      published: { bg: 'rgba(143,163,75,0.15)', bd: 'rgba(143,163,75,0.35)', fg: '#5B6E2C', label: 'Published' },
      draft: { bg: 'rgba(75,24,56,0.08)', bd: 'rgba(75,24,56,0.25)', fg: 'var(--heritage-plum)', label: 'Draft' },
      archived: { bg: 'rgba(75,24,56,0.15)', bd: 'rgba(75,24,56,0.35)', fg: 'var(--heritage-plum)', label: 'Archived' },
    };
    const s = map[status] || map.draft;
    return <span className="h-chip" style={{ background: s.bg, borderColor: s.bd, color: s.fg }}>{s.label}</span>;
  };

  return (
    <div className="space-y-6">
      <Panel className="p-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="h-eyebrow">Products</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Heritage Product CRM</div>
          <div className="text-sm opacity-80 mt-2 max-w-2xl" style={{ color: 'var(--heritage-plum)' }}>
            Full 18-field product editor. Save reusable description blocks as templates and load them on new products.
          </div>
        </div>
        <div className="flex gap-2">
          <button className="h-btn" onClick={() => setEditing('new')} data-testid="catalog-new">
            <Plus size={14} className="mr-1.5" /> New product
          </button>
        </div>
      </Panel>

      {toast && (
        <div className="fixed bottom-6 right-6 z-40 px-4 py-2 rounded-lg shadow-lg"
             style={{ background: 'var(--heritage-plum)', color: 'var(--heritage-cream)' }}
             data-testid="catalog-toast">
          {toast}
        </div>
      )}

      {editing && (
        <ItemForm
          initial={editing === 'new' ? EMPTY : editing}
          onSave={save}
          onDelete={editing !== 'new' ? del : undefined}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it, i) => {
          const img = it.image_base64 || it.image_url;
          return (
            <Panel key={it.id} className="p-3" data-testid={`catalog-card-${it.slug}`}>
              <div className="flex gap-4">
                <div className="w-28 h-28 rounded-lg overflow-hidden shrink-0" style={{ background: '#FBF8F1', border: '1px solid rgba(75,24,56,0.15)' }}>
                  {img ? (
                    <img src={img} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-40" style={{ color: 'var(--heritage-plum)' }}><ImageIcon size={22} /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-eyebrow">#{it.order_index}</span>
                    {statusPill(it)}
                    {it.founder_first && (
                      <span className="h-chip" style={{ background: 'rgba(154,75,126,0.12)', borderColor: 'rgba(154,75,126,0.3)', color: 'var(--heritage-plum)' }}>Founders first</span>
                    )}
                  </div>
                  <div className="h-serif text-xl mt-1 truncate" style={{ color: 'var(--heritage-plum)' }}>{it.name}</div>
                  <div className="text-xs opacity-80 truncate" style={{ color: 'var(--heritage-plum)' }}>{it.subtitle || it.category}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>${it.price} {it.currency}</div>
                    {it.sale_price && (
                      <div className="text-xs opacity-70" style={{ color: '#9A4B7E' }}>sale ${it.sale_price}</div>
                    )}
                    <div className="text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>· {it.inventory_qty ?? 0} in stock</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <button className="h-btn-outline text-xs" onClick={() => setEditing(it)} data-testid={`catalog-edit-${it.slug}`}>Edit</button>
                    <button className="h-btn-outline text-xs" onClick={() => toggleActive(it)}>
                      {it.active ? <><EyeOff size={12} className="mr-1" /> Hide</> : <><Eye size={12} className="mr-1" /> Show</>}
                    </button>
                    <button className="h-btn-outline text-xs" onClick={() => move(it.id, -1)} disabled={i === 0}><ArrowUp size={12} /></button>
                    <button className="h-btn-outline text-xs" onClick={() => move(it.id, 1)} disabled={i === items.length - 1}><ArrowDown size={12} /></button>
                    <a className="h-btn-outline text-xs" href={`/catalog/${it.slug}`} target="_blank" rel="noreferrer">
                      <FileText size={12} className="mr-1" /> View PDP
                    </a>
                  </div>
                </div>
              </div>
            </Panel>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-sm opacity-70" style={{ color: 'var(--heritage-plum)' }}>
            No products yet. Click &quot;New product&quot; to add your first.
          </div>
        )}
      </div>
    </div>
  );
}
